import {
  forwardRef,
  useId,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { cn } from '../../utils/cn';
import styles from './Field.module.css';
import type { FieldErrorLike } from './fieldUtils';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode;
  error?: FieldErrorLike | undefined;
  helper?: ReactNode;
  required?: boolean;
  options: SelectOption[];
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, helper, required, id: idProp, className, options, placeholder, ...rest },
  ref,
) {
  const reactId = useId();
  const id = idProp ?? `select-${reactId}`;
  const errorMsg = error?.message;
  const errorId = errorMsg ? `${id}-error` : undefined;
  const helperId = helper ? `${id}-helper` : undefined;
  const describedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn(styles.wrapper, className)}>
      {label !== undefined ? (
        <label htmlFor={id} className={styles.label}>
          {label}
          {required ? (
            <span className={styles.required} aria-hidden>
              *
            </span>
          ) : null}
        </label>
      ) : null}
      <select
        ref={ref}
        id={id}
        aria-invalid={errorMsg !== undefined || undefined}
        aria-describedby={describedBy}
        aria-required={required || undefined}
        className={cn(styles.control, errorMsg !== undefined && styles.invalid)}
        {...rest}
      >
        {placeholder !== undefined ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      {errorMsg ? (
        <p id={errorId} className={styles.error} role="alert">
          {errorMsg}
        </p>
      ) : helper ? (
        <p id={helperId} className={styles.helper}>
          {helper}
        </p>
      ) : null}
    </div>
  );
});
